import { useQuery, useMutation, useQueryClient } from '../lib/queryClient';
import { toast } from 'sonner';
import { getPersonasList, PersonaWithSettings } from '../endpoints/personas/list_GET.schema';
import { postPersonaSetting } from '../endpoints/personas/settings/update_POST.schema';
import {
  postCreatePersona,
  InputType as CreatePersonaInput,
} from '../endpoints/personas/create_POST.schema';
import {
  postUpdatePersona,
  InputType as UpdatePersonaInput,
} from '../endpoints/personas/update_POST.schema';
import { postDeletePersona } from '../endpoints/personas/delete_POST.schema';
import {
  postImportPersona,
  InputType as ImportPersonaInput,
} from '../endpoints/personas/import_POST.schema';
import { getExportPersona } from '../endpoints/personas/export_GET.schema';
import {
  postGeneratePersona,
  InputType as GeneratePersonaInput,
} from '../endpoints/personas/generate_POST.schema';
import { getSessionPersonas } from '../endpoints/personas/session/list_GET.schema';
import {
  postSessionPersonaSetting,
  InputType as SessionPersonaSettingInput,
} from '../endpoints/personas/session/update_POST.schema';
import { PersonaCategory } from './schema';

export interface UsePersonasOptions {
  category?: PersonaCategory;
  enabled?: boolean;
}

export const usePersonas = (options?: UsePersonasOptions) => {
  return useQuery({
    queryKey: ['personas', 'list', options?.category],
    queryFn: () => getPersonasList(options?.category ? { category: options.category } : {}),
    enabled: options?.enabled ?? true,
  });
};

export const useEnabledPersonas = (options?: Pick<UsePersonasOptions, 'enabled'>) => {
  const { data, isLoading, error } = usePersonas({ enabled: options?.enabled });

  const enabledPersonas = data?.personas.filter((p) => p.isEnabled) ?? [];

  return {
    personas: enabledPersonas,
    isLoading,
    error,
  };
};

export const usePersonasByCategory = () => {
  const { data, isLoading, error } = usePersonas();

  const byCategory = {
    development: [] as PersonaWithSettings[],
    security: [] as PersonaWithSettings[],
    operations: [] as PersonaWithSettings[],
    documentation: [] as PersonaWithSettings[],
    compliance: [] as PersonaWithSettings[],
    governance: [] as PersonaWithSettings[],
    custom: [] as PersonaWithSettings[],
  };

  if (data?.personas) {
    for (const persona of data.personas) {
      const category = persona.category as keyof typeof byCategory;
      if (byCategory[category]) {
        byCategory[category].push(persona);
      }
    }
  }

  return {
    byCategory,
    allPersonas: data?.personas ?? [],
    isLoading,
    error,
  };
};

export const useUpdatePersonaSetting = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postPersonaSetting,
    onSuccess: (data) => {
      if (data.setting.isEnabled) {
        toast.success('Persona enabled');
      } else {
        toast.success('Persona disabled');
      }
      queryClient.invalidateQueries({ queryKey: ['personas'] });
    },
    onError: (error) => {
      toast.error(`Failed to update persona setting: ${error.message}`);
    },
  });
};

export const useCreatePersona = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePersonaInput) => postCreatePersona(input),
    onSuccess: () => {
      toast.success('Persona created');
      queryClient.invalidateQueries({ queryKey: ['personas'] });
    },
    onError: (error) => {
      toast.error(`Failed to create persona: ${error.message}`);
    },
  });
};

export const useUpdatePersona = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdatePersonaInput) => postUpdatePersona(input),
    onSuccess: () => {
      toast.success('Persona updated');
      queryClient.invalidateQueries({ queryKey: ['personas'] });
    },
    onError: (error) => {
      toast.error(`Failed to update persona: ${error.message}`);
    },
  });
};

export const useDeletePersona = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => postDeletePersona({ id }),
    onSuccess: () => {
      toast.success('Persona deleted');
      queryClient.invalidateQueries({ queryKey: ['personas'] });
    },
    onError: (error) => {
      toast.error(`Failed to delete persona: ${error.message}`);
    },
  });
};

// Import/Export hooks
export const useImportPersona = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ImportPersonaInput) => postImportPersona(input),
    onSuccess: () => {
      toast.success('Persona imported successfully');
      queryClient.invalidateQueries({ queryKey: ['personas'] });
    },
    onError: (error) => {
      toast.error(`Failed to import persona: ${error.message}`);
    },
  });
};

export const useExportPersona = () => {
  return useMutation({
    mutationFn: (id: string) => getExportPersona({ id }),
    onError: (error) => {
      toast.error(`Failed to export persona: ${error.message}`);
    },
  });
};

// AI Generation hook
export const useGeneratePersona = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: GeneratePersonaInput) => postGeneratePersona(input),
    onSuccess: () => {
      toast.success('Persona generated successfully');
      queryClient.invalidateQueries({ queryKey: ['personas'] });
    },
    onError: (error) => {
      toast.error(`Failed to generate persona: ${error.message}`);
    },
  });
};

// Session-level persona hooks
export const useSessionPersonas = (sessionId: string | undefined) => {
  return useQuery({
    queryKey: ['personas', 'session', sessionId],
    queryFn: () =>
      sessionId ? getSessionPersonas({ sessionId }) : Promise.resolve({ personas: [] }),
    enabled: !!sessionId,
  });
};

export const useUpdateSessionPersonaSetting = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SessionPersonaSettingInput) => postSessionPersonaSetting(input),
    onSuccess: (data) => {
      if (data.setting.isEnabled) {
        toast.success('Persona enabled for session participants');
      } else {
        toast.success('Persona disabled for session participants');
      }
      queryClient.invalidateQueries({ queryKey: ['personas', 'session'] });
    },
    onError: (error) => {
      toast.error(`Failed to update session persona: ${error.message}`);
    },
  });
};
